import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Loader2,
  Search,
  AlertCircle,
  AlertTriangle,
  BookOpen,
  Database,
  ExternalLink,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { peptideDataMap } from "@/data/peptidesData";

interface VectorShiftResponse {
  text: string;
  html: string;
}

// Quick suggestion component for common queries
function QuickSuggestion({
  keyword,
  onClick,
}: {
  keyword: string;
  onClick: (keyword: string) => void;
}) {
  return (
    <Button
      variant="outline"
      size="sm"
      className="mr-2 mb-2 text-sm"
      onClick={() => onClick(keyword)}
    >
      <Search className="h-3 w-3 mr-1" />
      {keyword}
    </Button>
  );
}

// Component for suggestions based on detected peptide keywords
function PeptideSuggestions({
  query,
  onPeptideClick,
}: {
  query: string;
  onPeptideClick: (path: string) => void;
}) {
  const [detectedPeptides, setDetectedPeptides] = useState<string[]>([]);

  // List of peptide keywords to look for
  const peptideKeywords = Object.keys(peptideDataMap);

  // Detect peptide keywords in the query
  useEffect(() => {
    const lowerQuery = query.toLowerCase();
    const detected = peptideKeywords.filter((peptide) => {
      // Check for various forms of the peptide name
      const peptideLower = peptide.toLowerCase();
      const fullName = peptideDataMap[peptide].fullName.toLowerCase();

      return (
        lowerQuery.includes(peptideLower) ||
        lowerQuery.includes(fullName) ||
        // Special case for LL-37 which might be written as LL37
        (peptide === "ll37" && lowerQuery.includes("ll-37")) ||
        (peptide === "bpc157" && lowerQuery.includes("bpc-157"))
      );
    });

    setDetectedPeptides(detected);
  }, [query]);

  if (detectedPeptides.length === 0) return null;

  return (
    <div className="mt-6 p-4 border border-blue-200 bg-blue-50 rounded-md">
      <div className="flex items-center mb-2">
        <DatabaseIcon className="h-5 w-5 text-blue-700 mr-2" />
        <h3 className="text-blue-700 font-semibold">
          Found Peptide Information
        </h3>
      </div>

      <p className="mb-3">
        We detected references to the following peptides in your query. You can
        explore their detailed profiles:
      </p>

      <div className="flex flex-wrap gap-2">
        {detectedPeptides.map((peptide) => (
          <Button
            key={peptide}
            variant="secondary"
            size="sm"
            className="flex items-center"
            onClick={() => onPeptideClick(`/peptide-protocols/${peptide}`)}
          >
            <BookOpen className="h-3.5 w-3.5 mr-1" />
            {peptideDataMap[peptide].name}
            <ExternalLink className="h-3 w-3 ml-1" />
          </Button>
        ))}
      </div>
    </div>
  );
}

// Lucide icons with proper types
const DatabaseIcon = (props: React.ComponentProps<typeof Database>) => (
  <Database {...props} />
);

const AlertCircleIcon = (props: React.ComponentProps<typeof AlertCircle>) => (
  <AlertCircle {...props} />
);

export function VectorShiftQuery() {
  const [query, setQuery] = useState("");
  const [conversationId, setConversationId] = useState(`session_${Date.now()}`);
  const [activeTab, setActiveTab] = useState("rendered");
  const [apiAttempted, setApiAttempted] = useState(false);
  const { toast } = useToast();

  const [retryCount, setRetryCount] = useState(0);
  const [timeoutOccurred, setTimeoutOccurred] = useState(false);
  const [serverError, setServerError] = useState<boolean>(false);

  const { data, isLoading, error, refetch, isFetching, failureCount } =
    useQuery({
      queryKey: ["vectorshift", query, conversationId, retryCount],
      queryFn: async () => {
        if (!query.trim()) {
          throw new Error("Please enter a query");
        }

        setApiAttempted(true);
        setTimeoutOccurred(false);
        setServerError(false);

        try {
          // Create a promise with a 45 second client-side timeout
          const timeoutPromise = new Promise<never>((_, reject) => {
            const id = setTimeout(() => {
              clearTimeout(id);
              reject(new Error("Client-side timeout: Request took too long"));
            }, 45000); // 45 second client-side timeout
          });

          // Actual fetch request
          const fetchPromise = fetch("/api/vectorshift/query", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ query, conversationId }),
            credentials: "include",
          }).then(async (response) => {
            let result;

            try {
              // Try to parse the JSON response, but handle cases where it might not be valid JSON
              result = await response.json();
            } catch (e) {
              // If JSON parsing fails, create a fallback response object
              result = {
                text:
                  response.status === 500
                    ? "The server encountered an issue processing your request."
                    : `Error ${response.status}: ${response.statusText}`,
                html:
                  response.status === 500
                    ? "<p>The server encountered an issue processing your request. This might be due to high demand or a temporary issue with our AI service.</p>"
                    : `<p>Error ${response.status}: ${response.statusText}</p>`,
              };
            }

            // Check for timeout/processing response (status 202)
            if (response.status === 202) {
              setTimeoutOccurred(true);
              return {
                text:
                  result.text || "Your query is taking longer than expected.",
                html:
                  result.html ||
                  "<p>Your query is taking longer than expected.</p>",
                isTimeout: true,
              };
            }

            // Handle various error states
            if (!response.ok) {
              if (
                response.status === 500 ||
                response.status === 502 ||
                response.status === 503
              ) {
                setServerError(true);
                return {
                  text:
                    result.text ||
                    "Server is experiencing high load. Please try again shortly.",
                  html:
                    result.html ||
                    "<p>Server is experiencing high load. Please try again shortly.</p>",
                  isError: true,
                };
              }

              throw new Error(result.text || response.statusText);
            }

            // Process successful response
            return {
              text: result.text || "No text response available",
              html: result.html || "<p>No HTML content available</p>",
              isTimeout: false,
              isError: false,
            };
          });

          // Race between the fetch and the timeout
          return await Promise.race([fetchPromise, timeoutPromise]);
        } catch (error) {
          console.error("Error in VectorShift query:", error);

          // Create a user-friendly error response
          if (
            error instanceof Error &&
            error.message.includes("Client-side timeout")
          ) {
            setTimeoutOccurred(true);
            return {
              text: "Your request is taking longer than expected. Please try again or simplify your query.",
              html: "<p>Your request is taking longer than expected. Please try again or simplify your query.</p>",
              isTimeout: true,
              isError: false,
            };
          }

          setServerError(true);
          return {
            text: "We're having trouble connecting to our AI service. Please try again shortly.",
            html: "<p>We're having trouble connecting to our AI service. Please try again shortly.</p>",
            isError: true,
            isTimeout: false,
          };
        }
      },
      enabled: false, // Don't run query on component mount
      retry: 2, // Reduced retry count since we handle errors differently now
      retryDelay: (attemptCount) => Math.min(1000 * 2 ** attemptCount, 8000), // Quicker retry schedule
      // Using gcTime (formerly cacheTime) of 1 hour to ensure cached successful results are available
      gcTime: 60 * 60 * 1000,
    });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) {
      toast({
        title: "Error",
        description: "Please enter a query",
        variant: "destructive",
      });
      return;
    }

    refetch();
  };

  const resetConversation = () => {
    setConversationId(`session_${Date.now()}`);
    toast({
      title: "Conversation Reset",
      description: "Started a new conversation session",
    });
  };

  const handleQuickSuggestion = (suggestion: string) => {
    setQuery(suggestion);
    // Don't auto-submit to give user a chance to modify
  };

  const navigateToPeptide = (path: string) => {
    // We can't use the Link component directly here since we're not in a JSX return
    // Using window.location would cause a full page reload
    // Using history.pushState doesn't trigger a re-render
    window.location.href = path;
  };

  // Define some quick suggestion topics
  const quickSuggestions = [
    "What is BPC-157?",
    "How does BPC-157 promote tissue healing?",
    "What is the recommended dosage for LL-37?",
    "Can Thymosin Alpha-1 help with immune function?",
    "What are the side effects of peptide therapies?",
    "How to properly reconstitute and store peptides?",
  ];

  return (
    <div className="w-full max-w-4xl mx-auto p-4">
      <Card className="p-6 shadow-lg">
        <h2 className="text-2xl font-semibold mb-4">
          Peptide Knowledge Assistant
        </h2>
        <p className="text-muted-foreground mb-4">
          Ask questions about peptides, protocols, mechanisms, and clinical
          applications.
        </p>

        {/* Quick suggestions */}
        <div className="mb-6">
          <p className="text-sm text-muted-foreground mb-2">
            Try asking about:
          </p>
          <div className="flex flex-wrap">
            {quickSuggestions.map((suggestion, index) => (
              <QuickSuggestion
                key={index}
                keyword={suggestion}
                onClick={handleQuickSuggestion}
              />
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Textarea
            placeholder="e.g., What is the mechanism of BPC-157? How should I dose LL-37 for gut health?"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="min-h-[100px] resize-y"
            disabled={isLoading || isFetching}
          />

          <div className="flex justify-between">
            <Button
              type="submit"
              disabled={isLoading || isFetching || !query.trim()}
              className="w-1/2"
            >
              {(isLoading || isFetching) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {isLoading || isFetching ? "Processing..." : "Submit Query"}
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={resetConversation}
              className="ml-2"
            >
              New Conversation
            </Button>
          </div>
        </form>

        {/* Show peptide suggestions if we can detect them in the query */}
        {query.trim() && (
          <PeptideSuggestions
            query={query}
            onPeptideClick={navigateToPeptide}
          />
        )}

        {/* Enhanced loading experience */}
        {isLoading && (
          <div className="mt-6 p-5 bg-blue-50 border border-blue-100 rounded-md">
            <div className="flex items-start">
              <Loader2 className="h-5 w-5 text-blue-600 mr-3 animate-spin mt-0.5" />
              <div>
                <p className="text-blue-800 font-medium mb-2">
                  Processing your peptide query...
                </p>
                <div className="space-y-2 text-sm text-blue-700">
                  <p>
                    The AI is searching through scientific literature, clinical
                    protocols, and our extensive peptide database to give you
                    accurate information. This typically takes 30-60 seconds.
                  </p>
                  <div className="w-full bg-blue-100 rounded-full h-1.5 mt-3 overflow-hidden">
                    <div className="bg-blue-500 h-1.5 animate-pulse rounded-full"></div>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    Please don't refresh the page - your query is being
                    processed and will appear here when ready.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Appropriate error handling while still allowing retries */}
        {error && !data && apiAttempted && (
          <div className="mt-6 p-4 bg-amber-50 border border-amber-100 rounded-md">
            <div className="flex items-start">
              <AlertCircleIcon className="h-5 w-5 text-amber-600 mr-3 mt-0.5" />
              <div>
                <p className="text-amber-800 font-medium mb-1">
                  Still working on your query
                </p>
                <p className="text-sm text-amber-700">
                  Our system is taking longer than usual to process this query.
                  We're continuing to work on it and will display results as
                  soon as they're ready. Please keep this page open.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Special handling for timeout responses */}
        {data && data.isTimeout && (
          <div className="mt-6">
            <div className="p-5 bg-amber-50 border border-amber-200 rounded-md">
              <div className="flex items-start">
                <AlertCircleIcon className="h-5 w-5 text-amber-600 mr-3 mt-0.5" />
                <div>
                  <p className="text-amber-800 font-medium mb-2">
                    Query is taking longer than expected
                  </p>
                  <p className="text-sm text-amber-700 mb-3">
                    Your query is complex and our AI needs more time to process
                    it. You can try:
                  </p>
                  <ul className="text-sm text-amber-700 list-disc pl-5 mb-4 space-y-1">
                    <li>
                      Breaking your question into smaller, more specific parts
                    </li>
                    <li>
                      Using more direct language about the peptide information
                      you need
                    </li>
                    <li>
                      Retrying your query - sometimes our system may respond
                      faster on subsequent attempts
                    </li>
                  </ul>
                  <div className="flex gap-3 mt-4">
                    <Button
                      onClick={() => {
                        // Increment retry count to force a new query
                        setRetryCount((count) => count + 1);
                        refetch();
                      }}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      Retry Query
                    </Button>
                    <Button
                      variant="outline"
                      onClick={resetConversation}
                      className="border-amber-300"
                    >
                      Start New Conversation
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Special handling for server errors */}
        {data && data.isError && (
          <div className="mt-6">
            <div className="p-5 bg-rose-50 border border-rose-200 rounded-md">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-rose-600 mr-3 mt-0.5" />
                <div>
                  <p className="text-rose-800 font-medium mb-2">
                    Our AI service is currently experiencing difficulties
                  </p>
                  <p className="text-sm text-rose-700 mb-3">
                    We're experiencing high demand or a temporary issue with our
                    AI service. You can:
                  </p>
                  <ul className="text-sm text-rose-700 list-disc pl-5 mb-4 space-y-1">
                    <li>
                      Try again in a few minutes when our systems are less busy
                    </li>
                    <li>
                      Check our pre-written peptide protocol pages for
                      information
                    </li>
                    <li>Ask a more specific question about a single peptide</li>
                  </ul>
                  <div className="flex gap-3 mt-4">
                    <Button
                      onClick={() => {
                        // Increment retry count to force a new query
                        setRetryCount((count) => count + 1);
                        refetch();
                      }}
                      className="bg-rose-600 hover:bg-rose-700 text-white"
                    >
                      Retry Query
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => (window.location.href = "/peptides")}
                      className="border-rose-300"
                    >
                      View Peptide Protocols
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Show response data for successful responses */}
        {data && !data.isTimeout && !data.isError && (
          <div className="mt-6">
            <Separator className="my-4" />

            <Tabs
              defaultValue="rendered"
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="rendered">Formatted Response</TabsTrigger>
                <TabsTrigger value="text">Plain Text</TabsTrigger>
              </TabsList>

              <TabsContent value="text" className="mt-4">
                <Card className="p-4">
                  <pre className="whitespace-pre-wrap font-sans">
                    {data.text || "No text response available"}
                  </pre>
                </Card>
              </TabsContent>

              <TabsContent value="rendered" className="mt-4">
                <Card className="p-4 max-h-[600px] overflow-auto">
                  {data.html ? (
                    <div
                      className="peptide-html-content markdown-content"
                      dangerouslySetInnerHTML={{ __html: data.html }}
                      style={{
                        fontSize: "16px",
                        lineHeight: "1.6",
                        fontFamily: "system-ui, -apple-system, sans-serif",
                      }}
                    />
                  ) : (
                    <p className="text-muted-foreground">
                      No HTML content available
                    </p>
                  )}
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </Card>
    </div>
  );
}
